import React, { useEffect, useState, useRef } from "react";
import {
    Card,
    CardBody,
    Col,
    Container,
    Row,
    Button,
    InputGroup,
    Table,
    Input,
    Label
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import Breadcrumbs from "components/Common/Breadcrumb";
import { getLedgerReport } from "../../../helpers/UAM/report.helper"
import * as XLSX from 'xlsx';
import { formatNumberWithCommas, uId } from "common/utility";
import moment from 'moment';
import Select from "react-select";
import { getDealer } from "helpers/UAM/dealer.helper";
import _debounce from "lodash/debounce";
import ToastDialog from "common/ToastDialog"

const LedgerReport = () => {
    document.title = "Ledger Report | DMS";
    const [ledgerReport, setLedgerReport] = useState([])
    const [filters, setFilters] = useState(null);
    const flatpickrRef = useRef(null);
    const [selectedDealerName, setSelectedDealerName] = useState("");
    const [hasOpenedBefore, setHasOpenedBefore] = useState(true);
    const [checkboxes, setCheckboxes] = useState({
        openItem: false,
        closeItem: false,
        allItem: false,
    });
    const [dealerList, setDealerList] = useState([]);
    const user = JSON.parse(localStorage.getItem("user"));
    useEffect(() => {
        const bodyData = {
            searchKey: '',
        }
        getDealerList(bodyData);
    }, [])
    const [loading, setLoading] = useState(false)
    const [toastInfo, setToastInfo] = useState({ showToast: false })
    const [sortConfig, setSortConfig] = useState({ key: 'POSTING_DATE', direction: 'asc' });

    const fetchMTDSalesReport = (data) => {
        setLoading(true)
        getLedgerReport(data)
            .then((response) => {
                if (response.status && response.code == 200) {
                    const totalAmountInLC = response.data.reduce((acc, item) => {
                        return acc + (item.AMOUNT_IN_LC || 0);
                    }, 0);
                    let obj = {
                        DOCUMENT_NUMBER: 'Total',
                        AMOUNT_IN_LC: totalAmountInLC
                    }
                    response.data.push(obj)
                    sortData(response.data, sortConfig);
                    setLoading(false)
                } else {
                    setLoading(false)
                    setLedgerReport([])
                }
            })
            .catch((err) => {
                setLoading(false)
                console.log(err);
            })
    }

    const handleDateChange = (selectedDates) => {
        if (selectedDates.length === 2) {
            setFilters({
                startDate: selectedDates[0],
                endDate: selectedDates[1],
            })
        } else if (checkboxes.openItem && selectedDates.length === 1) {
            setFilters({
                startDate: '',
                endDate: selectedDates[0],
            })
        }
    };

    const downloadExcel = () => {
        let dataWithModifiedCol = ledgerReport.map(report => {
            let newReport = {
                "Account": report.DOCUMENT_NUMBER || '-',
                "Type": report.DOCUMENT_TYPE || '-',
                "Year": report.FISCAL_YEAR || '-',
                "Document date": moment(report.POSTING_DATE, 'YYYYMMDD').isValid() ? moment(report.POSTING_DATE, 'YYYYMMDD').format('DD-MM-YYYY') : '-',
                "Net due date": moment(report.NET_DUE_DATE, 'YYYYMMDD').isValid() ? moment(report.NET_DUE_DATE, 'YYYYMMDD').format('DD-MM-YYYY') : '-',
                "Reference": report.REFERENCE || '-',
                "LC amount": formatNumberWithCommas(report.AMOUNT_IN_LC) || '-',
                "Text": report.TEXT || '-',
            }
            return newReport
        })

        const workbook = XLSX.utils.book_new();
        const sheetData = XLSX.utils.json_to_sheet(dataWithModifiedCol);
        XLSX.utils.book_append_sheet(workbook, sheetData, 'LedgerReport');
        XLSX.writeFile(workbook, 'Ledger_Report.xlsx');
    };

    const debouncedGetDealerList = _debounce((value) => {
        const bodyData = {
            searchKey: value,
        };
        getDealerList(bodyData);
    }, 300);

    const handleInputChange = (value) => {
        debouncedGetDealerList(value);
    };

    const handleAnchorChange = (option) => {
        setSelectedDealerName(option);
    };

    const customeFilterSection = (
        <div className="d-flex align-items-center justify-content-start gap-2">
            <InputGroup style={{ minWidth: '250px' }}>
                <Flatpickr
                    ref={flatpickrRef}
                    className="form-control"
                    placeholder={checkboxes.openItem ? "Select Date" : "Start Date - End Date"}
                    options={{
                        mode: checkboxes.openItem ? 'single' : 'range',
                        dateFormat: 'd-m-Y',
                    }}
                    value={
                        checkboxes.openItem
                            ? filters?.startDate || ''
                            : filters?.startDate && filters?.endDate
                                ? [filters?.startDate, filters?.endDate]
                                : []
                    }
                    onChange={handleDateChange}
                />
            </InputGroup>

        </div>
    )

    const handleRadioChange = (e) => {
        const { value } = e.target;

        setCheckboxes({
            openItem: value === 'openItem',
            closeItem: value === 'closeItem',
            allItem: value === 'allItem',
        });
        
        if (value == 'openItem') {
            setFilters(null)
            if (flatpickrRef.current) {
                flatpickrRef.current.flatpickr.clear();
            }
        }
    };

    const handleDateFilter = () => {
        let obj = {
            openItem: checkboxes.openItem,
            closeItem: checkboxes.closeItem,
            allItem: checkboxes.allItem,
            low: selectedDealerName?.value || ''
        }
        if (filters?.startDate) {
            const startDate = moment(filters.startDate).format('YYYYMMDD');
            obj.fromDate = startDate
        }
        if (filters?.endDate) {
            const endDate = moment(filters.endDate).format('YYYYMMDD');
            obj.toDate = endDate
        }
        
        let errorMessage = '';
        if ((!filters?.startDate || !filters?.endDate) && !checkboxes.openItem) {
            errorMessage = 'Please Select Date';
        }
        if (user?.userType === 'employee' && !selectedDealerName) {
            errorMessage = errorMessage ? `${errorMessage} and Dealer` : 'Please Select Dealer';
        }
        if (errorMessage) {
            setToastInfo({
                message: errorMessage,
                showToast: true,
                role: "error",
            });
        } else {
            fetchMTDSalesReport(obj);
        }
    };

    const handleClearFilter = () => {
        setCheckboxes(
            {
                openItem: false,
                closeItem: false,
                allItem: false,
            }
        )
        setFilters(null)
        if (flatpickrRef.current) {
            flatpickrRef.current.flatpickr.clear();
        }
        setSelectedDealerName('')
    };

    const getDealerList = async (bodyData) => {
        const user = JSON.parse(localStorage.getItem("user"));
        if (user?.userType == 'employee') {
            const response = await getDealer(bodyData);
            if (response?.status) {
                setDealerList(response.data.data);
            } else {
                setDealerList([])
            }
        } else {
            setDealerList([])
        }
    }

    const buttonSection = (
        <div className="text-sm-end">

            <Button
                type="button"
                color="success"
                className="btn-rounded mb-2 me-2"
                onClick={downloadExcel}

            >
                <i className="bx bx-archive-in me-1" />
                {'Download'}
            </Button>

        </div>);

    const handleSort = (key) => {
        let direction = sortConfig.direction;

        if (sortConfig.key === key) {
            direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
        } else {
            direction = 'asc';
        }

        setSortConfig({ key, direction });
        sortData(ledgerReport, { key, direction });
    };

    const sortData = (data, sortParams) => {
        const { key, direction } = sortParams;

        const sortedData = [...data].sort((a, b) => {
            const formatDate = (dateStr) =>
                new Date(dateStr?.slice(0, 4) + '-' + dateStr?.slice(4, 6) + '-' + dateStr?.slice(6, 8)).getTime();

            const primaryA = key === 'POSTING_DATE' ? formatDate(a.POSTING_DATE) : a.REFERENCE;
            const primaryB = key === 'POSTING_DATE' ? formatDate(b.POSTING_DATE) : b.REFERENCE;

            if (primaryA < primaryB) return direction === 'asc' ? -1 : 1;
            if (primaryA > primaryB) return direction === 'asc' ? 1 : -1;

            if (a.REFERENCE < b.REFERENCE) return -1;
            if (a.REFERENCE > b.REFERENCE) return 1;

            return 0;
        });

        const totalRow = sortedData.find(item => item.DOCUMENT_NUMBER === 'Total');
        const finalData = totalRow
            ? [...sortedData.filter(item => item.DOCUMENT_NUMBER !== 'Total'), totalRow]
            : sortedData;

        setLedgerReport(finalData);
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return 'bx bx-chevron-down ps-1 pe-1 font-size-20 fw-bolder';
        return sortConfig.direction === 'asc'
            ? 'bx bx-chevron-up ps-1 pe-1 font-size-20 fw-bolder'
            : 'bx bx-chevron-down ps-1 pe-1 font-size-20 fw-bolder';
    };

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumbs title="Reports" breadcrumbItem="Ledger Report" />
                <Card>
                    <CardBody>
                        <Row>
                            {user?.userType == 'employee' && (<Col sm="12" md='12' lg='3' className="mb-3">
                                <Select
                                    options={dealerList?.map((option, index) => ({
                                        label: option.dealerName + '/' + option.dealerCode,
                                        value: option.dealerCode,
                                    }))}
                                    name="dealer"
                                    id="dealer"
                                    className='select-dropdown'
                                    placeholder={"Select Dealer"}
                                    value={selectedDealerName}
                                    onInputChange={(e) => {
                                        handleInputChange(e)
                                    }}
                                    onMenuOpen={(e) => {
                                        hasOpenedBefore ? handleInputChange("") : null;
                                        setHasOpenedBefore(false);
                                    }}
                                    onChange={(selectedOption) => {
                                        setSelectedDealerName(selectedOption.label)
                                        handleAnchorChange(selectedOption);
                                    }}
                                />
                            </Col>)}
                            <Col sm="12" md='12' lg='3' className="mb-3">{customeFilterSection}</Col>
                            <Col sm="12" md='12' lg='3' className="mb-3">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="form-check">
                                        <Input
                                            type="radio"
                                            className="form-check-input"
                                            id="openItem"
                                            name="item"
                                            value="openItem"
                                            checked={checkboxes.openItem}
                                            onChange={handleRadioChange}
                                        />
                                        <Label className="form-check-label" htmlFor="openItem">
                                            OPEN ITEM
                                        </Label>
                                    </div>
                                    <div className="form-check">
                                        <Input
                                            type="radio"
                                            className="form-check-input"
                                            id="closeItem"
                                            name="item"
                                            value="closeItem"
                                            checked={checkboxes.closeItem}
                                            onChange={handleRadioChange}
                                        />
                                        <Label className="form-check-label" htmlFor="closeItem">
                                            CLOSE ITEM
                                        </Label>
                                    </div>
                                    <div className="form-check">
                                        <Input
                                            type="radio"
                                            className="form-check-input"
                                            id="allItem"
                                            name="item"
                                            value="allItem"
                                            checked={checkboxes.allItem}
                                            onChange={handleRadioChange}
                                        />
                                        <Label className="form-check-label" htmlFor="allItem">
                                            ALL ITEM
                                        </Label>
                                    </div>
                                </div>


                            </Col>
                            <Col sm="12" md='12' lg={user?.userType == 'employee' ? 3 : 6} className="mb-3">
                                <div className="d-flex align-items-center justify-content-end gap-2">
                                    <button type="button"
                                        className="btn btn-success save-customer"
                                        onClick={handleDateFilter}
                                    >
                                        {loading && (
                                            <span
                                                className="spinner-border spinner-border-sm ms-2 me-2"
                                                role="status"
                                                aria-hidden="true"
                                            ></span>
                                        )}
                                        {loading && <span>Appling</span>}
                                        {!loading && <span>Apply</span>}
                                    </button>
                                    <Button variant="primary"
                                        onClick={handleClearFilter}
                                        outline
                                    >
                                        Clear
                                    </Button>
                                    {ledgerReport?.length > 0 && (<Button
                                        type="button"
                                        color="success"
                                        className="btn-rounded mb-0 me-2"
                                        onClick={downloadExcel}

                                    >
                                        <i className="bx bx-archive-in me-1" />
                                        {'Download'}
                                    </Button>)}
                                </div>
                            </Col>
                            <Col xs="12">
                                <div className="table-responsive">
                                    <Table className="table table-bordered mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Account</th>
                                                <th>Type</th>
                                                <th>Year</th>
                                                <th onClick={() => handleSort('POSTING_DATE')}>
                                                    Document date <i className={getSortIcon('POSTING_DATE')}></i>
                                                </th>
                                                <th>Net due date</th>
                                                <th onClick={() => handleSort('REFERENCE')}>
                                                    Reference <i className={getSortIcon('REFERENCE')}></i>
                                                </th>
                                                <th>LC amount</th>
                                                <th>Text</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {ledgerReport?.length == 0 ?
                                                <tr>
                                                    <td colSpan={12} className="text-center">No data found</td>
                                                </tr> :
                                                (ledgerReport?.map((elem, index) => (
                                                    <tr key={uId()}>
                                                        <td className={`${index % 2 == 0
                                                            ? "table-row-odd"
                                                            : "table-row-even"
                                                            }`}> {elem.DOCUMENT_NUMBER === "Total" ? (
                                                                <strong>{elem.DOCUMENT_NUMBER}</strong>
                                                            ) : (
                                                                elem.DOCUMENT_NUMBER
                                                            )}</td>
                                                        <td className={`${index % 2 == 0
                                                            ? "table-row-odd"
                                                            : "table-row-even"
                                                            }`}>{elem.DOCUMENT_TYPE}</td>
                                                        <td className={`${index % 2 == 0
                                                            ? "table-row-odd"
                                                            : "table-row-even"
                                                            }`}>{elem.FISCAL_YEAR}</td>
                                                        <td className={`${index % 2 == 0
                                                            ? "table-row-odd"
                                                            : "table-row-even"
                                                            }`}>{moment(elem.POSTING_DATE, 'YYYYMMDD').isValid() ? moment(elem.POSTING_DATE, 'YYYYMMDD').format('DD-MM-YYYY') : '-'}</td>
                                                        <td className={`${index % 2 == 0
                                                            ? "table-row-odd"
                                                            : "table-row-even"
                                                            }`}>{moment(elem.NET_DUE_DATE, 'YYYYMMDD').isValid() ? moment(elem.NET_DUE_DATE, 'YYYYMMDD').format('DD-MM-YYYY') : '-'}</td>
                                                        <td className={`${index % 2 == 0
                                                            ? "table-row-odd"
                                                            : "table-row-even"
                                                            }`}>{elem.REFERENCE}</td>
                                                        <td className={`${index % 2 == 0
                                                            ? "table-row-odd"
                                                            : "table-row-even"
                                                            }`}>{elem.DOCUMENT_NUMBER === "Total" ? (
                                                                <strong>{formatNumberWithCommas(elem.AMOUNT_IN_LC)}</strong>
                                                            ) : (
                                                                formatNumberWithCommas(elem.AMOUNT_IN_LC)
                                                            )}</td>
                                                        <td className={`${index % 2 == 0
                                                            ? "table-row-odd"
                                                            : "table-row-even"
                                                            }`}>{elem.TEXT}</td>
                                                    </tr>
                                                )))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

            </Container>
            <ToastDialog
                message={toastInfo.message || ""}
                showToast={toastInfo.showToast}
                role={toastInfo.role}
                onCloseFn={() => setToastInfo({ showToast: false })}
            ></ToastDialog>
        </div>
    );
};

export default LedgerReport;
